export type MediaSourceInput = string | {preview?: string; uri?: string; url?: string; src?: string; type?: string; mime?: string; name?: string; path?: string} | null | undefined;
export type ResolvedMediaSource = {kind: 'hls' | 'native' | 'vimeo-embed' | 'none'; url: string; mime?: string};

export function mediaSourceUrl(input: MediaSourceInput): string {
  return (typeof input === 'string' ? input : input?.preview || input?.uri || input?.url || input?.src || '').trim();
}

export function vimeoEmbedUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol)) return undefined;
    const match = ['vimeo.com', 'www.vimeo.com'].includes(url.hostname)
      ? url.pathname.match(/^\/(\d+)(?:\/([a-zA-Z0-9]+))?\/?$/)
      : url.hostname === 'player.vimeo.com' ? url.pathname.match(/^\/video\/(\d+)\/?$/) : null;
    if (!match) return undefined;
    const hash = match[2] || url.searchParams.get('h');
    return `https://player.vimeo.com/video/${match[1]}${hash ? `?h=${encodeURIComponent(hash)}` : ''}`;
  } catch {
    return undefined;
  }
}

function resolveSource(input: MediaSourceInput, audio: boolean): ResolvedMediaSource {
  const url = mediaSourceUrl(input);
  const mime = typeof input === 'object' && input ? input.type || input.mime : undefined;
  const path = url.split(/[?#]/)[0];
  if (!url || /\/missing\./i.test(path) || /^(?:javascript|data):/i.test(url)) return {kind: 'none', url: ''};
  const local = /^(?:blob:|file:)/i.test(url);
  const name = typeof input === 'object' && input ? input.name || input.path || '' : '';
  const hls = /\.m3u8$/i.test(path) || /(?:mpegurl)/i.test(mime || '') || (local && /\.m3u8$/i.test(name));
  if (hls) return {kind: local ? 'none' : 'hls', url, mime: 'application/vnd.apple.mpegurl'};
  const embed = !audio && vimeoEmbedUrl(url);
  if (embed) return {kind: 'vimeo-embed', url: embed};
  if (/^https?:\/\/(?:www\.|player\.)?vimeo\.com(?:\/|$)/i.test(url)) return {kind: 'none', url};
  const mapping: Record<string, string> = audio
    ? {mp3: 'audio/mpeg', m4a: 'audio/mp4', ogg: 'audio/ogg', wav: 'audio/wav', aac: 'audio/aac'}
    : {mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm'};
  const extension = path.split('.').pop()?.toLowerCase() || '';
  return {kind: 'native', url, mime: mime?.startsWith(audio ? 'audio/' : 'video/') ? mime : mapping[extension]};
}

export const resolveVideoSource = (input: MediaSourceInput): ResolvedMediaSource => resolveSource(input, false);
export const resolveAudioSource = (input: MediaSourceInput): ResolvedMediaSource => resolveSource(input, true);
